// IntradayScan.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import './IntradayScan.css';
import WatchlistStar from './WatchlistStar';
import RefreshButton from './RefreshButton';
import StockLogo from './StockLogo';

const formatTime = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

const getCurrentTimeInMinutes = () => {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
};

const IntradayScan = ({ onBack }) => {
  // Single active feature state (instead of multiple booleans)
  const [activeFeature, setActiveFeature] = useState('turnover'); // 'turnover' | 'dayHighLow' | 'surge' | 'volume' | 'gainers'

  // Data states for each feature
  const [turnoverData, setTurnoverData] = useState([]);
  const [dayHighLowData, setDayHighLowData] = useState({ dayLow: [], dayHigh: [] });
  const [surgeData, setSurgeData] = useState([]);
  const [volumeData, setVolumeData] = useState([]);
  const [gainersLosersData, setGainersLosersData] = useState({ gainers: [], losers: [] });

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Sub-tab states
  const [dayHighLowTab, setDayHighLowTab] = useState('dayLow'); // 'dayLow' | 'dayHigh'
  const [surgeFilter, setSurgeFilter] = useState('ALL'); // 'ALL' | 'HIGH' | 'LOW'
  const [gainersLosersTab, setGainersLosersTab] = useState('gainers'); // 'gainers' | 'losers'

  // Time bar states for Intraday Surge
  const [sliderValue, setSliderValue] = useState(null);
  const [appliedTime, setAppliedTime] = useState(null);

  // Timeframe state for Volume Boost
  const [orbTimeframe, setOrbTimeframe] = useState('15'); // '15' | '30'

  // Fetch High Turnover Stocks
  const fetchTurnoverStocks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/high-turnover-stocks?top=30`);
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${res.status}`);
      }
      const response = await res.json();

      if (response.status !== 'success') {
        throw new Error(response.message || 'Failed to fetch high turnover stocks');
      }

      const stocks = Array.isArray(response.data) ? response.data : [];

      const formatted = stocks.map((stock, index) => {
        const symbol = stock?.symbol ?? '—';
        const turnoverCr = Number(stock?.turnover_cr) || 0;
        const ltp = Number(stock?.ltp) || 0;
        const percentageChange = Number(stock?.percentage_change) || 0;

        return {
          rank: index + 1,
          symbol,
          turnover: turnoverCr.toFixed(2),
          ltp: ltp.toFixed(2),
          percentageChange: percentageChange.toFixed(2),
        };
      });

      setTurnoverData(formatted);
    } catch (err) {
      setError(err.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Day High/Low Stocks
  const fetchDayHighLowStocks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/top-low-level`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'fo' }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const responseData = data.data || data;

      // Process day low stocks
      const dayLow = Array.isArray(responseData.low_performers)
        ? responseData.low_performers
          .filter(item => item.percentage_change < 0)
          .map((item, idx) => ({
            id: item.rank_near_low || idx + 1,
            name: item.stock || '—',
            ltp: item.ltp ? Number(item.ltp).toFixed(2) : '—',
            proximity: Number(item.proximity_to_low || item.proximity_to_day_low || 0).toFixed(2),
            change: `${Number(item.percentage_change).toFixed(2)}%`,
            highlight: item.rank_near_low === 1,
          }))
          .sort((a, b) => Number(b.proximity) - Number(a.proximity))
          .map((item, index) => ({
            ...item,
            rank: index + 1
          }))
        : [];

      // Process day high stocks
      const dayHigh = Array.isArray(responseData.top_performers)
        ? responseData.top_performers
          .filter(item => item.percentage_change > 0)
          .map((item, idx) => ({
            id: item.rank_near_high || item.rank || idx + 1,
            name: item.stock || '—',
            ltp: item.ltp ? Number(item.ltp).toFixed(2) : '—',
            proximity: Number(item.proximity_to_high || item.proximity_to_day_high || 0).toFixed(2),
            change: `+${Number(item.percentage_change).toFixed(2)}%`,
            highlight: (item.rank_near_high === 1) || (item.rank === 1),
          }))
          .sort((a, b) => Number(b.proximity) - Number(a.proximity))
          .map((item, index) => ({
            ...item,
            rank: index + 1
          }))
        : [];

      setDayHighLowData({ dayLow, dayHigh });
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Intraday Surge Data
  const fetchSurgeData = useCallback(async (timeStr) => {
    setLoading(true);
    setError('');
    try {
      let url = `${API_BASE_URL}/intraday/scan`;
      if (timeStr) {
        url += `?time=${encodeURIComponent(timeStr)}`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      if (json.intraday_boost_stocks) {
        console.log('Intraday Boost Data:', json.intraday_boost_stocks);
        setSurgeData(json.intraday_boost_stocks);
      } else if (json.success && json.data) {
        setSurgeData(json.data);
      } else {
        setSurgeData([]);
        setError(json.message || 'No data found');
      }
    } catch (err) {
      setError(err.message || 'Failed to load data.');
      setSurgeData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Volume Boost Data
  const fetchVolumeData = useCallback(async (timeframeStr) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/orb?timeframes=${timeframeStr}`);

      if (!response.ok) {
        throw new Error('Failed to fetch ORB data');
      }

      const data = await response.json();
      if (data.status === 'success') {
        setVolumeData(data.data || []);
      } else {
        throw new Error(data.message || 'Error fetching data');
      }
    } catch (err) {
      setError('Failed to load ORB data. ' + (err.message || 'Please try again later.'));
      setVolumeData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Top Gainers/Losers Data
  const fetchGainersLosersData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/top-gainers-losers`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const data = result.data;

      // Process top gainers
      const gainersData = Array.isArray(data.top_gainers)
        ? data.top_gainers.map((item, idx) => ({
          id: idx + 1,
          name: item.symbol || '—',
          ltp: item.ltp ? Number(item.ltp).toFixed(2) : '—',
          change: item.change_pct ? `${Number(item.change_pct).toFixed(2)}%` : '—',
          highlight: idx === 0
        }))
        : [];

      // Process top losers
      const losersData = Array.isArray(data.top_losers)
        ? data.top_losers.map((item, idx) => ({
          id: idx + 1,
          name: item.symbol || '—',
          ltp: item.ltp ? Number(item.ltp).toFixed(2) : '—',
          change: item.change_pct ? `${Number(item.change_pct).toFixed(2)}%` : '—',
          highlight: idx === 0
        }))
        : [];

      setGainersLosersData({ gainers: gainersData, losers: losersData });
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data when active feature changes
  useEffect(() => {
    switch (activeFeature) {
      case 'turnover':
        fetchTurnoverStocks();
        break;
      case 'dayHighLow':
        fetchDayHighLowStocks();
        break;
      case 'surge':
        fetchSurgeData(appliedTime);
        break;
      case 'volume':
        fetchVolumeData(orbTimeframe);
        break;
      case 'gainers':
        fetchGainersLosersData();
        break;
      default:
        break;
    }
  }, [activeFeature, appliedTime, orbTimeframe, fetchTurnoverStocks, fetchDayHighLowStocks, fetchSurgeData, fetchVolumeData, fetchGainersLosersData]);

  // Refresh handler based on active feature
  const handleRefresh = () => {
    switch (activeFeature) {
      case 'turnover':
        fetchTurnoverStocks();
        break;
      case 'dayHighLow':
        fetchDayHighLowStocks();
        break;
      case 'surge':
        fetchSurgeData(appliedTime);
        break;
      case 'volume':
        fetchVolumeData(orbTimeframe);
        break;
      case 'gainers':
        fetchGainersLosersData();
        break;
      default:
        break;
    }
  };

  // Render table based on active feature
  const renderTable = () => {
    if (loading) {
      return <div className="loading">⏳ Loading stock data...</div>;
    }

    if (error) {
      return <div className="error-message">⚠️ {error}</div>;
    }

    switch (activeFeature) {
      case 'turnover':
        return (
          <div className="high-turnover-table">
            <table>
              <colgroup>
                <col style={{ width: '15%' }} />
                <col style={{ width: '35%' }} />
                <col style={{ width: '35%' }} />
                <col style={{ width: '15%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Symbol</th>
                  <th>Turnover (₹ Cr)</th>
                  <th>LTP</th>
                </tr>
              </thead>
              <tbody>
                {turnoverData.map((stock) => (
                  <tr key={`${stock.symbol}-${stock.rank}`}>
                    <td style={{ textAlign: 'center' }}>{stock.rank}</td>
                    <td style={{ textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <WatchlistStar
                          stock={{
                            symbol: stock.symbol,
                            name: stock.symbol
                          }}
                        />
                        <StockLogo symbol={stock.symbol} />
                        {stock.symbol}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>{stock.turnover}</td>
                    <td style={{ textAlign: 'center' }}>{stock.ltp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'dayHighLow':
        const currentData = dayHighLowTab === 'dayLow' ? dayHighLowData.dayLow : dayHighLowData.dayHigh;
        return (
          <div className="high-turnover-table">
            <table>
              <colgroup>
                <col style={{ width: '10%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '15%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>LTP</th>
                  <th>{dayHighLowTab === 'dayHigh' ? 'Proximity to Day High' : 'Proximity to Day Low'}</th>
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((stock) => {
                  const isPositive = stock.change && !stock.change.includes('-');
                  return (
                    <tr key={`${dayHighLowTab}-${stock.id}`} className={stock.highlight ? 'highlight' : ''}>
                      <td style={{ textAlign: 'center' }}>{stock.rank}</td>
                      <td style={{ textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <WatchlistStar
                            stock={{
                              symbol: stock.name,
                              name: stock.name
                            }}
                          />
                          <StockLogo symbol={stock.name} />
                          {stock.name}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>{stock.ltp}</td>
                      <td style={{ textAlign: 'center' }}>{stock.proximity}</td>
                      <td style={{ textAlign: 'center' }} className={isPositive ? 'positive' : 'negative'}>
                        {stock.change}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );

      case 'surge':
        const filteredSurgeData = surgeData.filter(item => {
          if (surgeFilter === 'ALL') return true;
          const pct = Number(item.percent_change || item.pct_change || 0);
          if (surgeFilter === 'HIGH') return pct >= 0;
          if (surgeFilter === 'LOW') return pct < 0;
          return true;
        });
        return (
          <div className="high-turnover-table">
            <table>
              <colgroup>
                <col style={{ width: '10%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Symbol</th>
                  <th>LTP</th>
                  <th>% Change</th>
                  <th>Z Score</th>
                  <th>Signal</th>
                </tr>
              </thead>
              <tbody>
                {filteredSurgeData.map((r, idx) => {
                  const pct = Number(r.percent_change || r.pct_change || 0);
                  const fac = r.Rfac ?? r['r factor'] ?? r.r_factor ?? r.rfactor ?? r.fac ?? r.z_score ?? r.rfac;
                  const ltp = r.ltp;
                  const symbol = (r.stock || '').toUpperCase();
                  const isCE = symbol.includes('CE');
                  const isPE = symbol.includes('PE');

                  // Determine signal based on CE/PE or percentage change fallback
                  let signal = '';
                  let signalColor = '';

                  if (isCE) {
                    signal = '↓';
                    signalColor = '#ef4444'; // Red for CE down
                  } else if (isPE) {
                    signal = '↑';
                    signalColor = '#10b981'; // Green for PE up
                  } else {
                    const isPositive = pct >= 0;
                    signal = isPositive ? '↑' : '↓';
                    signalColor = isPositive ? '#10b981' : '#ef4444';
                  }

                  return (
                    <tr key={`${r.stock}-${idx}`}>
                      <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                      <td style={{ textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <WatchlistStar
                            stock={{
                              symbol: r.stock,
                              name: r.stock
                            }}
                          />
                          <StockLogo symbol={r.stock} />
                          {r.stock}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>{ltp ?? '—'}</td>
                      <td style={{ textAlign: 'center' }} className={pct >= 0 ? 'positive' : 'negative'}>
                        {Number.isFinite(pct) ? `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%` : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>{fac !== undefined && fac !== null ? Number(fac).toFixed(2) : '—'}</td>
                      <td style={{ textAlign: 'center', color: signalColor, fontWeight: 'bold' }}>
                        {signal}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );

      case 'volume':
        return (
          <div className="high-turnover-table">
            <table>
              <colgroup>
                <col style={{ width: '8%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '17%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Stock</th>
                  <th>TF</th>
                  <th>Dir</th>
                  <th>Breakout</th>
                  <th>Entry</th>
                  <th>SL</th>
                  <th>R-Fac</th>
                  <th>LTP (Opt)</th>
                </tr>
              </thead>
              <tbody>
                {volumeData.map((stock, idx) => {
                  const isPositive = stock['ORB Direction'] === 'Bullish';
                  const isNegative = stock['ORB Direction'] === 'Bearish';
                  const rFac = parseFloat(stock['R-fac']) || 0;
                  const rFacColor = rFac >= 1.5 ? '#10b981' : rFac >= 1.0 ? '#fbbf24' : '#ef4444';
                  const ltp = isPositive ? stock['CE LTP'] : isNegative ? stock['PE LTP'] : '—';

                  return (
                    <tr key={`${stock.Stock}-${stock.Timeframe}-${idx}`}>
                      <td style={{ textAlign: 'center' }}>{stock.Rank}</td>
                      <td style={{ textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <WatchlistStar
                            stock={{
                              symbol: stock.Stock,
                              name: stock.Stock
                            }}
                          />
                          <StockLogo symbol={stock.Stock} />
                          {stock.Stock}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>{stock.Timeframe}</td>
                      <td style={{ textAlign: 'center', color: isPositive ? '#10b981' : isNegative ? '#ef4444' : 'inherit', fontWeight: 'bold' }}>
                        {isPositive ? '↑ Bull' : isNegative ? '↓ Bear' : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>{stock['Breakout Time']}</td>
                      <td style={{ textAlign: 'center' }}>{stock['ORB Entry']}</td>
                      <td style={{ textAlign: 'center' }}>{stock['ORB SL']}</td>
                      <td style={{ textAlign: 'center', color: rFacColor, fontWeight: 'bold' }}>
                        {rFac.toFixed(2)}
                      </td>
                      <td style={{ textAlign: 'center' }}>{ltp}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );

      case 'gainers':
        const currentGainersData = gainersLosersTab === 'gainers' ? gainersLosersData.gainers : gainersLosersData.losers;
        return (
          <div className="high-turnover-table">
            <table>
              <colgroup>
                <col style={{ width: '15%' }} />
                <col style={{ width: '35%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '25%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>LTP</th>
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                {currentGainersData.map((row) => {
                  const isPositive = row.change && !row.change.includes('-');
                  return (
                    <tr key={row.id} className={row.highlight ? 'highlight' : ''}>
                      <td style={{ textAlign: 'center' }}>{row.highlight ? '⭐' : row.id}</td>
                      <td style={{ textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <WatchlistStar
                            stock={{
                              symbol: row.name,
                              name: row.name
                            }}
                          />
                          <StockLogo symbol={row.name} />
                          {row.name}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>₹{row.ltp}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`change-badge ${isPositive ? 'positive' : 'negative'}`}>
                          {row.change}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );

      default:
        return null;
    }
  };

  // Render sub-filters based on active feature
  const renderSubFilters = () => {
    switch (activeFeature) {
      case 'dayHighLow':
        return (
          <div className="sub-filters">
            <button
              className={`sub-filter-btn ${dayHighLowTab === 'dayLow' ? 'active' : ''}`}
              onClick={() => setDayHighLowTab('dayLow')}
            >
              Day Low Stocks
            </button>
            <button
              className={`sub-filter-btn ${dayHighLowTab === 'dayHigh' ? 'active' : ''}`}
              onClick={() => setDayHighLowTab('dayHigh')}
            >
              Day High Stocks
            </button>
          </div>
        );
      case 'surge': {
        const currentTimeMins = getCurrentTimeInMinutes();
        const maxSliderValue = Math.min(930, Math.max(555, currentTimeMins));
        
        return (
          <div className="surge-filters-container">
            <div className="sub-filters" style={{ margin: 0 }}>
              <button
                className={`sub-filter-btn ${surgeFilter === 'ALL' ? 'active' : ''}`}
                onClick={() => setSurgeFilter('ALL')}
              >
                ALL
              </button>
              <button
                className={`sub-filter-btn ${surgeFilter === 'HIGH' ? 'active' : ''}`}
                onClick={() => setSurgeFilter('HIGH')}
              >
                UP
              </button>
              <button
                className={`sub-filter-btn ${surgeFilter === 'LOW' ? 'active' : ''}`}
                onClick={() => setSurgeFilter('LOW')}
              >
                DOWN
              </button>
            </div>
            
            <div className="time-bar-container">
              <div className="time-bar-header">
                <span className="time-label">
                  Time: {sliderValue === null ? 'Real-time' : formatTime(sliderValue)}
                </span>
                <button 
                   className="sub-filter-btn reset-time-btn"
                   onClick={() => {
                     setSliderValue(null);
                     setAppliedTime(null);
                   }}
                >
                  Live
                </button>
              </div>
              <input 
                type="range" 
                className="time-slider"
                min="555" 
                max="930" 
                step="5"
                value={sliderValue !== null ? sliderValue : maxSliderValue}
                onChange={(e) => {
                  let val = Number(e.target.value);
                  if (val > maxSliderValue) val = maxSliderValue;
                  setSliderValue(val);
                }}
                onMouseUp={(e) => {
                  let val = Number(e.target.value);
                  if (val > maxSliderValue) val = maxSliderValue;
                  const newTime = formatTime(val);
                  if (newTime !== appliedTime) {
                    setAppliedTime(newTime);
                  }
                }}
                onTouchEnd={(e) => {
                  let val = Number(e.target.value);
                  if (val > maxSliderValue) val = maxSliderValue;
                  const newTime = formatTime(val);
                  if (newTime !== appliedTime) {
                    setAppliedTime(newTime);
                  }
                }}
              />
              <div className="time-bar-labels">
                <span>09:15</span>
                <span>15:30</span>
              </div>
            </div>
          </div>
        );
      }
      case 'volume':
        return (
          <div className="sub-filters">
            <button
              className={`sub-filter-btn ${orbTimeframe === '15' ? 'active' : ''}`}
              onClick={() => setOrbTimeframe('15')}
            >
              15 Min
            </button>
            <button
              className={`sub-filter-btn ${orbTimeframe === '30' ? 'active' : ''}`}
              onClick={() => setOrbTimeframe('30')}
            >
              30 Min
            </button>
          </div>
        );
      case 'gainers':
        return (
          <div className="sub-filters">
            <button
              className={`sub-filter-btn ${gainersLosersTab === 'gainers' ? 'active' : ''}`}
              onClick={() => setGainersLosersTab('gainers')}
            >
              Top Gainers 📈
            </button>
            <button
              className={`sub-filter-btn ${gainersLosersTab === 'losers' ? 'active' : ''}`}
              onClick={() => setGainersLosersTab('losers')}
            >
              Top Losers 📉
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="intraday-page">
      {/* Header */}
      <header className="intraday-header">
        <div className="intraday-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U" />
          </div>
          <div className="intraday-user">
            <RefreshButton
              onRefresh={handleRefresh}
              isLoading={loading}
              size="small"
              style={{ marginRight: '10px' }}
            />
          </div>
        </div>
      </header>

      {/* Page Content */}
      <main className="intraday-main">
        <div className="intraday-top">
          <div className="s4u-title-row">
            <div className="s4u-page-heading intraday-heading-override">
              <h1 className="page-title intraday-glowing-title">
                Intraday Scan <span className="info-icon">ℹ️</span>
              </h1>
              <button className="s4u-back-btn intraday-back-btn" onClick={onBack}>← Back to Dashboard</button>
            </div>
          </div>

          {/* Feature Categories */}
          <div className="categories">
            <button
              className={activeFeature === 'turnover' ? 'active' : ''}
              onClick={() => setActiveFeature('turnover')}
            >
              High Turnover Stocks
            </button>
            <button
              className={activeFeature === 'dayHighLow' ? 'active' : ''}
              onClick={() => setActiveFeature('dayHighLow')}
            >
              Day High and Low Stocks
            </button>
            <button
              className={activeFeature === 'surge' ? 'active' : ''}
              onClick={() => setActiveFeature('surge')}
            >
              Intraday Surge
            </button>
            <button
              className={activeFeature === 'volume' ? 'active' : ''}
              onClick={() => setActiveFeature('volume')}
            >
              Volume Boost
            </button>
            <button
              className={activeFeature === 'gainers' ? 'active' : ''}
              onClick={() => setActiveFeature('gainers')}
            >
              Top Gainers & Losers
            </button>
          </div>

          {/* Sub-filters for features that need them */}
          {renderSubFilters()}
        </div>

        {/* Table Section */}
        <div className="intraday-table-container">
          {renderTable()}
        </div>
      </main>
    </div>
  );
};

export default IntradayScan;
